import React, { useMemo, useState } from 'react';
import { Button, Card, Flex, Select, Table, Tag, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CalculatorData } from '../calculator/Calculator';
import { useAppColors } from '../../shared/theme';
import { POINTS_GROUP_THRESHOLD_A, POINTS_GROUP_THRESHOLD_B, type DifferentiationLevel, type FragilityLevel } from '../../shared/lib/scoring/constants';
import type { CriterionScore } from '../../shared/lib/scoring/goNogo';
import { scoreCalculation, type ManualCriteria } from './scoringInput';
import HintLabel from '../../shared/ui/HintLabel';

interface CriteriaScoreProps {
  data?: CalculatorData;
  launchBudget: number;
  manual: ManualCriteria;
  onManualChange: (next: ManualCriteria) => void;
}

const FRAGILITY: FragilityLevel[] = ['low', 'medium', 'high'];
const DIFFERENTIATION: DifferentiationLevel[] = ['high', 'medium', 'low', 'none'];

/**
 * «Оценка по критериям» (WS 15587410, п.3; фидбек 29.09): быстрый вывод «стоит ли делать
 * запуск» всегда на виду, таблица критериев — по кнопке. Считается сразу при правке
 * калькулятора тем же ядром, что в Launch Pipeline (`shared/lib/scoring`).
 */
const CriteriaScore: React.FC<CriteriaScoreProps> = ({ data, launchBudget, manual, onManualChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const c = useAppColors();
  const [showTable, setShowTable] = useState(false);
  const score = useMemo(() => scoreCalculation(data, launchBudget, manual), [data, launchBudget, manual]);

  const tone =
    score.verdict === 'GO'
      ? { bg: c.successBg, fg: c.success }
      : score.verdict === 'CONDITIONAL'
        ? { bg: c.warningBg, fg: c.warning }
        : score.verdict === 'NO_GO'
          ? { bg: c.errorBg, fg: c.error }
          : { bg: token.colorFillQuaternary, fg: token.colorTextSecondary };

  const title = !score.hasData
    ? t('idea_calc.scoring.verdict_pending')
    : score.autoReject
      ? t('idea_calc.scoring.verdict_auto_reject', { reason: t(`idea_calc.scoring.auto_reject.${score.autoReject}`) })
      : score.isPartial
        ? // Без ниши вердикт — по доле баллов, а группа — по абсолютной сумме: вместе они
          // спорят («запускать — группа В»), поэтому группу до привязки ниши не показываем.
          t(`idea_calc.scoring.verdict_partial.${score.verdict}`)
        : t(`idea_calc.scoring.verdict.${score.verdict}`, { group: score.group ? t(`idea_calc.scoring.group.${score.group}`) : '' });

  const counted = score.breakdown.filter((b) => b.counted).length;

  return (
    <Card id="score" title={t('idea_calc.scoring.title')} variant="outlined" style={{ scrollMarginTop: 72 }}>
      <Flex vertical gap="middle">
        <Flex gap="middle" wrap align="stretch">
          <div style={{ flex: '2 1 360px', padding: token.padding, borderRadius: token.borderRadiusLG, background: tone.bg }}>
            <Typography.Text style={{ color: tone.fg, display: 'block' }}><HintLabel hint={t('idea_calc.scoring.tips.verdict')}>{t('idea_calc.scoring.verdict_label')}</HintLabel></Typography.Text>
            <Typography.Text strong style={{ color: tone.fg, fontSize: token.fontSizeHeading3, display: 'block' }} data-testid="scoring-verdict">
              {title}
            </Typography.Text>
            {score.isPartial && score.hasData && (
              <Typography.Text style={{ color: tone.fg }}>{t('idea_calc.scoring.partial_hint')}</Typography.Text>
            )}
          </div>
          <div style={{ flex: '1 1 200px', padding: token.padding, borderRadius: token.borderRadiusLG, background: token.colorFillQuaternary, border: `1px solid ${token.colorBorderSecondary}` }}>
            <Typography.Text type="secondary" style={{ display: 'block' }}><HintLabel hint={t('idea_calc.scoring.tips.points')}>{t('idea_calc.scoring.points_label')}</HintLabel></Typography.Text>
            <Typography.Text strong style={{ fontSize: token.fontSizeHeading2, display: 'block' }} data-testid="scoring-points">
              {score.hasData ? score.totalPoints : '—'}
            </Typography.Text>
            <Typography.Text type="secondary">
              {score.isPartial
                ? t('idea_calc.scoring.percent_hint', { percent: score.percentCriteria })
                : t('idea_calc.scoring.groups_hint', { a: POINTS_GROUP_THRESHOLD_A, b: POINTS_GROUP_THRESHOLD_B })}
            </Typography.Text>
          </div>
        </Flex>

        <Flex gap="middle" wrap align="center">
          <Flex align="center" gap="small">
            <Typography.Text><HintLabel hint={t('idea_calc.scoring.tips.criterion.fragility')}>{t('idea_calc.scoring.fragility')}</HintLabel></Typography.Text>
            <Select
              style={{ minWidth: 150 }}
              allowClear
              placeholder={t('idea_calc.scoring.not_set')}
              value={manual.fragility ?? undefined}
              onChange={(v?: FragilityLevel) => onManualChange({ ...manual, fragility: v ?? null })}
              options={FRAGILITY.map((v) => ({ value: v, label: t(`idea_calc.scoring.fragility_level.${v}`) }))}
            />
          </Flex>
          <Flex align="center" gap="small">
            <Typography.Text><HintLabel hint={t('idea_calc.scoring.tips.criterion.differentiation')}>{t('idea_calc.scoring.differentiation')}</HintLabel></Typography.Text>
            <Select
              style={{ minWidth: 150 }}
              allowClear
              placeholder={t('idea_calc.scoring.not_set')}
              value={manual.differentiation ?? undefined}
              onChange={(v?: DifferentiationLevel) => onManualChange({ ...manual, differentiation: v ?? null })}
              options={DIFFERENTIATION.map((v) => ({ value: v, label: t(`idea_calc.scoring.differentiation_level.${v}`) }))}
            />
          </Flex>
          <div style={{ flex: '1 1 auto' }} />
          <Tag style={{ color: token.colorTextSecondary }}><HintLabel hint={t('idea_calc.scoring.tips.counted')}>{t('idea_calc.scoring.counted', { counted, total: score.totalCriteria })}</HintLabel></Tag>
          <Button onClick={() => setShowTable(!showTable)} aria-expanded={showTable}>
            {showTable ? t('idea_calc.scoring.hide_table') : t('idea_calc.scoring.show_table')}
          </Button>
        </Flex>

        {showTable && (
          <Table<CriterionScore>
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={score.breakdown}
            columns={[
              { key: 'name', title: t('idea_calc.scoring.col_criterion'), render: (_, r) => <HintLabel hint={t(`idea_calc.scoring.tips.criterion.${r.id}`)}>{t(`idea_calc.scoring.criterion.${r.id}`)}</HintLabel> },
              { key: 'block', title: t('idea_calc.scoring.col_block'), render: (_, r) => t(`idea_calc.scoring.block.${r.block}`) },
              { key: 'mult', title: <HintLabel hint={t('idea_calc.scoring.tips.col_multiplier')}>{t('idea_calc.scoring.col_multiplier')}</HintLabel>, align: 'right', render: (_, r) => `×${r.multiplier}` },
              {
                key: 'raw',
                title: <HintLabel hint={t('idea_calc.scoring.tips.col_score')}>{t('idea_calc.scoring.col_score')}</HintLabel>,
                align: 'right',
                render: (_, r) => (r.counted && r.rawScore !== null ? `${Math.round(r.rawScore * 100)}%` : t('idea_calc.scoring.no_data')),
              },
              {
                key: 'points',
                title: <HintLabel hint={t('idea_calc.scoring.tips.col_points')}>{t('idea_calc.scoring.col_points')}</HintLabel>,
                align: 'right',
                render: (_, r) => (r.counted ? <Typography.Text strong>{Math.round(r.points)}</Typography.Text> : '—'),
              },
            ]}
          />
        )}
      </Flex>
    </Card>
  );
};

export default CriteriaScore;
